import * as Blockly from 'blockly/core';

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    alpha: number;
    decay: number;
    color: string;
}

interface DeleteCandidate {
    x: number;
    y: number;
    color: string;
}

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let container: HTMLElement | null = null;
let particles: Particle[] = [];
let animId: number | null = null;
let candidate: DeleteCandidate | null = null;
let ro: ResizeObserver | null = null;

export function initBlockParticle(el: HTMLElement): void {
    disposeBlockParticle();

    container = el;
    canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:100;';
    el.appendChild(canvas);

    const c = canvas.getContext('2d');
    if (!c) return;
    ctx = c;

    syncSize();

    ro = new ResizeObserver(syncSize);
    ro.observe(el);
}

export function disposeBlockParticle(): void {
    ro?.disconnect();
    ro = null;
    if (animId !== null) {
        cancelAnimationFrame(animId);
        animId = null;
    }
    canvas?.remove();
    canvas = null;
    ctx = null;
    container = null;
    particles = [];
    candidate = null;
}

function syncSize(): void {
    if (!canvas || !container) return;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
}

export function captureDeleteCandidate(ws: Blockly.WorkspaceSvg, blockId: string | undefined): void {
    candidate = null;
    if (!blockId) return;

    const block = ws.getBlockById(blockId) as Blockly.BlockSvg | null;
    if (!block) return;

    const wsPos = block.getRelativeToSurfaceXY();
    const scale = ws.getScale();
    const metrics = ws.getMetricsManager().getViewMetrics(true);

    const bw = (block as any).width ?? 100;
    const bh = (block as any).height ?? 30;
    const cx = wsPos.x + bw / 2;
    const cy = wsPos.y + bh / 2;

    const svgEl = ws.getParentSvg();
    const svgRect = svgEl.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();
    if (!containerRect) return;

    const svgX = (cx - metrics.left) * scale;
    const svgY = (cy - metrics.top) * scale;

    candidate = {
        x: svgRect.left - containerRect.left + svgX,
        y: svgRect.top - containerRect.top + svgY,
        color: block.getColour(),
    };
}

export function triggerDeleteEffect(): void {
    if (!candidate || !canvas || !ctx) return;
    spawnParticles(candidate.x, candidate.y, candidate.color);
    flashCanvas();
    candidate = null;
    if (animId === null) animId = requestAnimationFrame(tick);
}

function spawnParticles(x: number, y: number, baseColor: string): void {
    const colors = [baseColor, '#ffcc00', '#ff6600', '#ff4444', '#ffaa33', '#ffffff', '#ffdd88'];
    for (let i = 0; i < 52; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 4;
        particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - 1,
            r: 1.5 + Math.random() * 3,
            alpha: 1,
            decay: 0.018 + Math.random() * 0.022,
            color: colors[Math.floor(Math.random() * colors.length)],
        });
    }
}

function tick(): void {
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles = particles.filter((p) => p.alpha > 0.01);

    for (const p of particles) {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15;
        p.vx *= 0.97;
        p.r *= 0.975;
        p.alpha -= p.decay;
    }

    if (particles.length > 0) {
        animId = requestAnimationFrame(tick);
    } else {
        animId = null;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
}

let flashEl: HTMLDivElement | null = null;
let flashTimer: ReturnType<typeof setTimeout> | null = null;

function flashCanvas(): void {
    if (!container) return;

    if (!flashEl) {
        flashEl = document.createElement('div');
        flashEl.style.cssText =
            'position:absolute;inset:0;pointer-events:none;z-index:99;transition:background 0.45s ease-out;background:rgba(255,80,40,0);';
        container.appendChild(flashEl);
    }

    if (flashTimer) clearTimeout(flashTimer);

    flashEl.style.transition = 'none';
    flashEl.style.background = 'rgba(255,80,40,0.15)';

    requestAnimationFrame(() => {
        if (!flashEl) return;
        flashEl.style.transition = 'background 0.45s ease-out';
        flashEl.style.background = 'rgba(255,80,40,0)';
    });

    flashTimer = setTimeout(() => {
        flashTimer = null;
    }, 500);
}
